<template>
  <BaseModal
    v-if="showModal"
    :heading="$t('treatwell.intent_reminder.heading')"
    small
    testId="treatwell-intent-reminder"
    @close="dismiss"
  >
    <Flex direction="column">
      <BaseText
        >{{ $t('treatwell.intent_reminder.description', brandName()) }}</BaseText
      >
      <Flex
        direction="column"
        :gap="0.5"
      >
        <div
          v-for="reason in ['reason_1', 'reason_2', 'reason_3']"
          :key="reason"
          :class="$style.bulletPoint"
        >
          <BaseIcon
            name="check"
            color="success"
            size="l"
          />
          {{ $t(`treatwell.intent_reminder.${reason}`) }}
        </div>
      </Flex>
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="revokeTreatwellIntent"
        v-test="'tw-revoke-intent'"
      >
        {{ $t('treatwell.intent_reminder.no_longer_interested') }}
      </BaseButton>
      <BaseButton
        :loading="loading"
        @click="openContractModal"
        v-test="'intent-submit'"
      >
        {{ $t('treatwell.intent_reminder.get_started') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from '@/dayjs';
import { brandName } from '@/helpers/brand-name';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import { UPDATE_TREATWELL_INTENT } from '../graphql';

const { isSubscribed } = useCompanyStore();
const { treatwellStatus, showTreatwell } = useTreatwellStore();

const dismissedAt = useStorage('twIntentReminderDismissedAt', '');
const router = useRouter();

const showModal = computed(
  () =>
    showTreatwell &&
    treatwellStatus.subscriptionIntention &&
    !treatwellStatus.contractSigned &&
    isSubscribed &&
    (!dismissedAt.value ||
      dayjs().subtract(3, 'day').isAfter(dismissedAt.value)),
);

const { mutate: revokeTreatwellIntent, onDone } = useMutation(
  UPDATE_TREATWELL_INTENT,
  {
    variables: {
      input: {
        subscriptionIntention: false,
      },
    },
  },
);

onDone(() => {
  dismiss();
});

const { mutate, loading } = useMutation(
  gql`
    mutation treatwellIntegrationRequestCreate(
      $input: IntegrationRequestCreateInput!
    ) {
      treatwellIntegrationRequestCreate(input: $input) {
        treatwellIntegrationRequest {
          id
        }
      }
    }
  `,
  {
    variables: {
      input: {},
    },
  },
);

const goToContract = () => {
  router.push({ name: 'treatwell-admin' });
};

const openContractModal = () => {
  if (!treatwellStatus.hasRequest) {
    mutate().then(() => {
      dismiss();
      goToContract();
      treatwellStatus.hasRequest = true;
    });
  } else {
    dismiss();
    goToContract();
  }
};

const dismiss = () => (dismissedAt.value = dayjs().format());
</script>

<style
  lang="scss"
  module
>
.bulletPoint {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}
</style>
